import React, { useEffect, useState } from "react";
import {
  listLeagueClips,
  createLeagueClip,
  deleteLeagueClip,
  listLeagueStreams,
  upsertLeagueStream,
  endLeagueStream,
  listLeagueActiveChallenges,
} from "../lib/api";

/**
 * LeagueClipsPanel — drops into LeagueDetailModal as the CLIPS tab.
 *
 * Two sections:
 *   1. LIVE STREAMS — currently active stream URLs (Twitch/YouTube).
 *      Casters/owners/co-admins can set or end a stream per match.
 *   2. CLIPS — newest first list of submitted clips. Click → opens
 *      the URL in a new tab. Poster + admins can remove a clip.
 *
 * The "Post stream" + "Post clip" composers only render when the
 * viewer has the necessary role (canPost flag passed in from parent).
 */
export function LeagueClipsPanel({ league, canPost, isAdmin, onToast, currentUserId, activeChallenges }) {
  const [streams, setStreams] = useState([]);
  const [clips, setClips] = useState([]);
  const [innerChallenges, setInnerChallenges] = useState([]);
  // Use the prop if the caller already has it; otherwise fetch our own.
  const challenges = (activeChallenges && activeChallenges.length > 0)
    ? activeChallenges
    : innerChallenges;
  const [busy, setBusy] = useState(false);
  const [showClipForm, setShowClipForm] = useState(false);
  const [showStreamForm, setShowStreamForm] = useState(false);

  // Composer state
  const [clipTitle, setClipTitle] = useState("");
  const [clipUrl, setClipUrl] = useState("");
  const [clipChallenge, setClipChallenge] = useState("");
  const [streamChallenge, setStreamChallenge] = useState("");
  const [streamUrl, setStreamUrl] = useState("");
  const [streamTitle, setStreamTitle] = useState("");

  const load = async () => {
    if (!league?.league_id) return;
    setBusy(true);
    try {
      const [s, c, ch] = await Promise.all([
        listLeagueStreams(league.league_id),
        listLeagueClips(league.league_id),
        // Fetch active challenges lazily — caller may have passed them.
        (activeChallenges && activeChallenges.length > 0)
          ? Promise.resolve({ challenges: activeChallenges })
          : listLeagueActiveChallenges(league.league_id).catch(() => ({ challenges: [] })),
      ]);
      setStreams(s.streams || []);
      setClips(c.clips || []);
      setInnerChallenges(ch.challenges || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load streams/clips.");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [league?.league_id]);

  const postStream = async () => {
    if (!streamChallenge || !streamUrl.trim()) return;
    setBusy(true);
    try {
      await upsertLeagueStream(league.league_id, {
        challenge_id: streamChallenge,
        stream_url: streamUrl.trim(),
        title: streamTitle.trim() || null,
      });
      onToast?.("📺 Stream is live!");
      setShowStreamForm(false);
      setStreamUrl(""); setStreamTitle(""); setStreamChallenge("");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't post stream.");
    } finally { setBusy(false); }
  };

  const endStream = async (cid) => {
    if (!window.confirm("End this stream?")) return;
    setBusy(true);
    try {
      await endLeagueStream(league.league_id, cid);
      onToast?.("📺 Stream ended.");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't end stream.");
    } finally { setBusy(false); }
  };

  const postClip = async () => {
    if (!clipTitle.trim() || !clipUrl.trim()) return;
    setBusy(true);
    try {
      await createLeagueClip(league.league_id, {
        title: clipTitle.trim(),
        clip_url: clipUrl.trim(),
        challenge_id: clipChallenge || null,
      });
      onToast?.("🎬 Clip posted.");
      setShowClipForm(false);
      setClipTitle(""); setClipUrl(""); setClipChallenge("");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't post clip.");
    } finally { setBusy(false); }
  };

  const removeClip = async (clip) => {
    if (!window.confirm(`Delete "${clip.title}"?`)) return;
    setBusy(true);
    try {
      await deleteLeagueClip(league.league_id, clip.clip_id);
      onToast?.("🗑 Clip deleted.");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't delete.");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="league-clips-panel" className="space-y-5">
      {/* ───── LIVE STREAMS ───── */}
      <section data-testid="league-streams-section">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] tracking-[3px] font-bold text-red-400">
            <i className="fas fa-circle text-red-500 mr-1.5 animate-pulse" />LIVE STREAMS
          </p>
          {canPost && !showStreamForm && (
            <button
              type="button"
              onClick={() => setShowStreamForm(true)}
              data-testid="open-stream-form"
              className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
            >
              <i className="fas fa-broadcast-tower mr-1" />GO LIVE
            </button>
          )}
        </div>

        {showStreamForm && (
          <div className="glass rounded-2xl p-3 mb-3 border border-red-400/35 space-y-2" data-testid="stream-form">
            {challenges?.length > 0 ? (
              <select
                value={streamChallenge}
                onChange={(e) => setStreamChallenge(e.target.value)}
                data-testid="stream-challenge-select"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
              >
                <option value="">— Pick a live match —</option>
                {challenges.map((c) => (
                  <option key={c.challenge_id} value={c.challenge_id}>
                    {c.from_team_name} vs {c.to_team_name}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-[11px] text-white/60">No accepted matches yet — schedule one first.</p>
            )}
            <input
              value={streamUrl}
              onChange={(e) => setStreamUrl(e.target.value)}
              placeholder="https://twitch.tv/your_channel  (or youtube.com)"
              data-testid="stream-url-input"
              className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm font-mono"
            />
            <input
              value={streamTitle}
              onChange={(e) => setStreamTitle(e.target.value)}
              placeholder="Stream title (optional)"
              maxLength={120}
              data-testid="stream-title-input"
              className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={postStream}
                disabled={busy || !streamChallenge || !streamUrl.trim()}
                data-testid="stream-submit"
                className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-40"
              >
                <i className="fas fa-tower-broadcast mr-1" />GO LIVE
              </button>
              <button
                type="button"
                onClick={() => setShowStreamForm(false)}
                data-testid="stream-cancel"
                className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
              >
                CANCEL
              </button>
            </div>
          </div>
        )}

        {streams.length === 0 ? (
          <p className="text-[12px] text-white/45 text-center py-3" data-testid="streams-empty">
            No live streams right now.
          </p>
        ) : (
          <div className="space-y-2" data-testid="streams-list">
            {streams.map((s) => (
              <div
                key={s.challenge_id}
                data-testid={`stream-row-${s.challenge_id}`}
                className="glass rounded-2xl p-3 border border-red-400/40 flex items-center gap-3"
              >
                <span className="text-[9px] tracking-widest px-2 py-1 rounded-full bg-red-500/20 text-red-300 border border-red-500/40 shrink-0">
                  <i className="fas fa-circle text-red-500 mr-1 animate-pulse" />LIVE
                </span>
                <div className="flex-1 min-w-0">
                  <a
                    href={s.stream_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`stream-watch-${s.challenge_id}`}
                    className="text-sm font-bold text-white hover:text-[#14b8a6] truncate block"
                  >
                    {s.title || s.stream_url}
                    <i className="fas fa-up-right-from-square text-[10px] ml-1.5 text-white/40" />
                  </a>
                  <p className="text-[10px] text-white/45">
                    Cast by {s.posted_by_name}
                  </p>
                </div>
                {canPost && (
                  <button
                    type="button"
                    onClick={() => endStream(s.challenge_id)}
                    disabled={busy}
                    data-testid={`stream-end-${s.challenge_id}`}
                    className="text-[10px] tracking-widest px-2.5 py-1.5 rounded-lg btn-ghost"
                  >
                    <i className="fas fa-circle-stop mr-1" />END
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ───── CLIPS ───── */}
      <section data-testid="league-clips-section">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] tracking-[3px] font-bold text-[#14b8a6]">
            <i className="fas fa-film mr-1.5" />CLIPS
          </p>
          {canPost && !showClipForm && (
            <button
              type="button"
              onClick={() => setShowClipForm(true)}
              data-testid="open-clip-form"
              className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
            >
              <i className="fas fa-plus mr-1" />POST CLIP
            </button>
          )}
        </div>

        {showClipForm && (
          <div className="glass rounded-2xl p-3 mb-3 border border-[#14b8a6]/35 space-y-2" data-testid="clip-form">
            <input
              value={clipTitle}
              onChange={(e) => setClipTitle(e.target.value)}
              placeholder="Clip title — e.g. INSANE 1v5"
              maxLength={120}
              data-testid="clip-title-input"
              className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
            />
            <input
              value={clipUrl}
              onChange={(e) => setClipUrl(e.target.value)}
              placeholder="https://clips.twitch.tv/...  or  https://youtube.com/..."
              data-testid="clip-url-input"
              className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm font-mono"
            />
            {challenges?.length > 0 && (
              <select
                value={clipChallenge}
                onChange={(e) => setClipChallenge(e.target.value)}
                data-testid="clip-challenge-select"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm"
              >
                <option value="">— Match (optional) —</option>
                {challenges.map((c) => (
                  <option key={c.challenge_id} value={c.challenge_id}>
                    {c.from_team_name} vs {c.to_team_name}
                  </option>
                ))}
              </select>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={postClip}
                disabled={busy || !clipTitle.trim() || !clipUrl.trim()}
                data-testid="clip-submit"
                className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-neon disabled:opacity-40"
              >
                <i className="fas fa-paper-plane mr-1" />POST CLIP
              </button>
              <button
                type="button"
                onClick={() => setShowClipForm(false)}
                data-testid="clip-cancel"
                className="text-[11px] tracking-widest px-4 py-2 rounded-xl btn-ghost"
              >
                CANCEL
              </button>
            </div>
          </div>
        )}

        {clips.length === 0 ? (
          <p className="text-[12px] text-white/45 text-center py-6" data-testid="clips-empty">
            No clips yet. {canPost ? "Drop the first one!" : ""}
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="clips-list">
            {clips.map((c) => (
              <ClipCard
                key={c.clip_id}
                clip={c}
                onOpen={(url) => window.open(url, "_blank", "noopener,noreferrer")}
                onDelete={(isAdmin || c.poster_user_id === currentUserId) ? () => removeClip(c) : null}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ClipCard({ clip, onOpen, onDelete }) {
  return (
    <div
      data-testid={`clip-card-${clip.clip_id}`}
      className="glass rounded-2xl p-3 border border-white/8 hover:border-[#14b8a6]/45 transition cursor-pointer"
      onClick={() => onOpen(clip.clip_url)}
    >
      <p className="text-sm font-bold text-white truncate">{clip.title}</p>
      <p className="text-[10px] text-white/45 mt-1 truncate">
        <i className="fas fa-user mr-1" />{clip.poster_name}
      </p>
      <p className="text-[10px] text-[#14b8a6] mt-1 truncate font-mono">
        {clip.clip_url}
      </p>
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          data-testid={`clip-delete-${clip.clip_id}`}
          aria-label="Delete clip"
          className="mt-2 text-[10px] tracking-widest px-2 py-1 rounded-lg bg-red-500/10 text-red-300 border border-red-500/40 hover:bg-red-500/20"
        >
          <i className="fas fa-trash mr-1" />DELETE
        </button>
      )}
    </div>
  );
}

export default LeagueClipsPanel;
